/**
 * Target frame (enemy side of the top `FrameCluster`, #421). Shows hostile
 * target hp bar or "no target" empty state.
 *
 * Layout (#421): no longer self-positions (`position: absolute` + a
 * hardcoded `left: %`) — renders as an ordinary flex child of
 * `FrameCluster`'s enemy (right) group, extending the same
 * friendly-left/enemy-right convention `UnitFrameStack` (#138) already used
 * for the bottom roster.
 *
 * Champion resolution (#420 fix): the resource bar + cast-bar spell name now
 * resolve against the TARGET'S OWN `championId` (`WireEntity.championId`,
 * #420) instead of the hardcoded `DEFAULT_HUD_CHAMPION_ID` placeholder —
 * same bug class as #275's own-frame regression. Any target now shows its
 * real ability name/icon/resource labels, not the local warrior's.
 *
 * WP8 e2e assertions:
 *   • data-testid="target-frame"
 *   • data-hp — integer hp value
 *   • data-max-hp — integer max hp
 *   • data-empty — "true" when no target
 */

import type { ReactElement } from "react";
import { useStore } from "../store/context.js";
import { targetEntity } from "./hud-util.js";
import { FrameCastBar } from "./FrameCastBar.js";
import { FrameResourceBar } from "./FrameResourceBar.js";
import { HpBar } from "./HpBar.js";

export function TargetFrame(): ReactElement {
  const { state } = useStore();
  const target = targetEntity(state.hudSnapshot, state.ownSeat);
  const isEmpty = target === null;
  return (
    <div
      data-testid="target-frame"
      data-empty={isEmpty ? "true" : "false"}
      data-hp={target === null ? "0" : String(target.hp)}
      data-max-hp={target === null ? "0" : String(target.maxHp)}
      style={{
        width: 200,
        padding: 8,
        background: "#161c24",
        border: "1px solid #444",
        borderRadius: 4,
        opacity: isEmpty ? 0.4 : 1,
        pointerEvents: "auto",
      }}
    >
      {isEmpty ? (
        <div style={{ fontSize: 12, opacity: 0.7 }}>no target</div>
      ) : (
        <>
          <div style={{ fontSize: 12, marginBottom: 4 }}>
            target · seat {String(target.seat)}
          </div>
          <HpBar hp={target.hp} maxHp={target.maxHp} testId="target-hp-bar" />
          <FrameResourceBar
            resource={target.resource}
            points={target.points}
            championId={target.championId}
            testIdPrefix="target"
          />
          <FrameCastBar
            cast={target.cast}
            tick={state.hudSnapshot?.tick ?? 0}
            championId={target.championId}
            testIdPrefix="target-frame"
          />
        </>
      )}
    </div>
  );
}
